import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Search as SearchIcon, FileText, Eye, Download, Lock, Star } from 'lucide-react';
import { docsSearch, docsCategories, docsDownload, docsRead, colSave, colRemove, colGet, apiErrText } from '../api';
import { useAuth } from '../context/AuthContext';

const CC=['bg-blue-100 text-blue-700','bg-green-100 text-green-700','bg-purple-100 text-purple-700','bg-orange-100 text-orange-700','bg-red-100 text-red-700','bg-yellow-100 text-yellow-700','bg-teal-100 text-teal-700','bg-pink-100 text-pink-700'];

export default function Search() {
  const { isPaid, isLoggedIn } = useAuth();
  const [params]           = useSearchParams();
  const [q, setQ]          = useState(params.get('q')||'');
  const [cat, setCat]      = useState('');
  const [docs, setDocs]    = useState([]);
  const [cats, setCats]    = useState([]);
  const [loading, setLoad] = useState(false);
  const [saved, setSaved]  = useState(new Set());
  const [msg, setMsg]      = useState('');

  useEffect(()=>{
    docsCategories().then(r=>setCats(['All',...r.data.categories])).catch(()=>{});
    if(isLoggedIn()) colGet().then(r=>setSaved(new Set((r.data.collection||[]).map(c=>c.document_id)))).catch(()=>{});
    if(params.get('q')) runSearch(params.get('q'),'');
  },[]);

  const runSearch = async (qv,cv) => {
    setLoad(true);
    try{ const r=await docsSearch(qv,cv==='All'?'':cv); setDocs(r.data.documents||[]); }
    catch{ setDocs([]); } finally{ setLoad(false); }
  };
  const handleSearch = ()=>runSearch(q,cat);
  const handleCat    = c=>{ setCat(c); runSearch(q,c); };

  const handleSave = async doc=>{
    if(!isLoggedIn()){ setMsg('Please login to save documents'); setTimeout(()=>setMsg(''),3000); return; }
    try{
      if(saved.has(doc.id)){
        await colRemove(doc.id);
        setSaved(s=>{ const n=new Set(s); n.delete(doc.id); return n; });
        setMsg('Removed from collection');
      }else{
        await colSave(doc.id);
        setSaved(s=>new Set([...s,doc.id]));
        setMsg('Saved to collection!');
      }
      setTimeout(()=>setMsg(''),2000);
    }
    catch{ setMsg('Something went wrong'); setTimeout(()=>setMsg(''),2000); }
  };

  const handleDownload = async doc=>{
    try{
      const r=await docsDownload(doc.id);
      const url=URL.createObjectURL(new Blob([r.data],{type:'application/pdf'}));
      const a=document.createElement('a'); a.href=url; a.download=doc.title+'.pdf'; a.click(); URL.revokeObjectURL(url);
    }catch(e){ alert('Download failed: '+await apiErrText(e)); }
  };

  const handleRead = async doc=>{
    const w=window.open('','_blank'); // open synchronously so popup blockers allow it
    if(w) w.document.write('<title>Loading document...</title><p style="font-family:sans-serif;padding:24px">Loading document, please wait...</p>');
    try{
      const r=await docsRead(doc.id);
      const url=URL.createObjectURL(new Blob([r.data],{type:'application/pdf'}));
      if(w) w.location=url; else window.open(url,'_blank');
    }catch(e){ if(w) w.close(); alert('Read failed: '+await apiErrText(e)); }
  };

  return(
    <div className="max-w-4xl mx-auto px-6 py-8">
      <h1 className="text-2xl font-bold text-blue-950 mb-6">Search Legal Documents</h1>
      {msg&&<div className="mb-4 text-sm px-4 py-2 rounded-xl bg-blue-50 border border-blue-200 text-blue-700">{msg}</div>}
      <div className="flex gap-2 mb-5">
        <input value={q} onChange={e=>setQ(e.target.value)} onKeyDown={e=>e.key==='Enter'&&handleSearch()} placeholder="Search by title, category, keyword..." className="flex-1 border border-gray-200 rounded-xl px-4 py-3 outline-none focus:border-blue-500 text-sm"/>
        <button onClick={handleSearch} className="bg-blue-900 text-white px-5 py-3 rounded-xl hover:bg-blue-800 flex items-center gap-2 text-sm font-semibold transition"><SearchIcon size={15}/>Search</button>
      </div>
      <div className="flex flex-wrap gap-2 mb-6">
        {cats.map(c=>(
          <button key={c} onClick={()=>handleCat(c)} className={"text-xs px-3 py-1.5 rounded-full border transition "+(cat===c?'bg-blue-900 text-white border-blue-900':'border-blue-200 text-blue-700 hover:bg-blue-50')}>{c}</button>
        ))}
      </div>
      {loading&&<div className="text-center py-20 text-gray-400">Searching...</div>}
      {!loading&&docs.length===0&&(q||cat)&&<div className="text-center py-20 text-gray-400">No documents found. Try a different keyword.</div>}
      {!loading&&docs.length===0&&!q&&!cat&&(
        <div className="text-center py-20 text-gray-400"><SearchIcon size={48} className="mx-auto mb-3 opacity-20"/><p>Enter a keyword or select a category to search</p></div>
      )}
      {docs.map((doc,i)=>(
        <div key={doc.id} className="bg-white border border-gray-100 rounded-2xl p-5 mb-4 shadow-sm hover:shadow-md transition">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                <FileText size={14} className="text-blue-500 shrink-0"/>
                <span className={"text-xs px-2 py-0.5 rounded-full font-medium "+CC[i%CC.length]}>{doc.category}</span>
                {doc.year&&<span className="text-xs text-gray-400">{doc.year}</span>}
              </div>
              <h3 className="font-bold text-gray-800 mb-1 truncate">{doc.title}</h3>
              <p className="text-xs text-gray-400">{doc.pages?`${doc.pages} pages · `:''}{doc.language}</p>
            </div>
            <div className="flex flex-col gap-2 shrink-0">
              {isPaid()?(
                <><button onClick={()=>handleDownload(doc)} className="flex items-center gap-1 text-xs bg-green-600 text-white px-3 py-1.5 rounded-lg hover:bg-green-500 transition"><Download size={11}/>Download</button>
                <button onClick={()=>handleRead(doc)} className="flex items-center gap-1 text-xs bg-blue-900 text-white px-3 py-1.5 rounded-lg hover:bg-blue-800 transition"><Eye size={11}/>Read</button></>
              ):(
                <Link to="/pricing" className="flex items-center gap-1 text-xs bg-gray-100 text-gray-500 px-3 py-1.5 rounded-lg hover:bg-yellow-50 hover:text-yellow-700 transition"><Lock size={11}/>Subscribe</Link>
              )}
              {isLoggedIn()&&(
                <button onClick={()=>handleSave(doc)} className={"flex items-center gap-1 text-xs border px-3 py-1.5 rounded-lg transition "+(saved.has(doc.id)?'border-yellow-400 bg-yellow-50 text-yellow-600':'border-gray-200 text-gray-500 hover:border-yellow-400 hover:text-yellow-600')}>
                  <Star size={11}/>{saved.has(doc.id)?'Saved':'Save'}
                </button>
              )}
            </div>
          </div>
        </div>
      ))}
      {!isPaid()&&docs.length>0&&(
        <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-5 text-center mt-2">
          <Lock size={22} className="mx-auto text-yellow-500 mb-2"/>
          <p className="font-semibold text-gray-700 mb-1">Unlock Full Access</p>
          <p className="text-sm text-gray-500 mb-3">Read and download all {docs.length}+ legal documents</p>
          <Link to="/pricing" className="bg-yellow-500 text-blue-950 px-5 py-2 rounded-xl text-sm font-bold hover:bg-yellow-400 transition">Subscribe Now — $49/year</Link>
        </div>
      )}
    </div>
  );
}
